import React, { useState, useEffect } from "react";
import styles from "./Popup.module.scss";
import { submitFeedback } from "../../services/branchService";
import privacyPolicyPdf from "../../assets/Политика_конфиденциальности.pdf"; // Импортируем PDF файл

const Popup = ({ isOpen, onClose, onFormSubmit }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState(false); // Новое состояние для чекбокса

  const openPrivacyPolicy = (e) => {
    e.preventDefault();
    // Открываем PDF в новой вкладке используя импортированный файл
    window.open(privacyPolicyPdf, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");

    if (cleaned.length === 0) return "";

    let formatted = "+7 ";

    if (cleaned.length > 1) {
      formatted += `(${cleaned.substring(1, 4)}`;
    }
    if (cleaned.length >= 5) {
      formatted += `) ${cleaned.substring(4, 7)}`;
    }
    if (cleaned.length >= 8) {
      formatted += `-${cleaned.substring(7, 9)}`;
    }
    if (cleaned.length >= 10) {
      formatted += `-${cleaned.substring(9, 11)}`;
    }

    return formatted;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, "");

    if (cleaned.length > 11) return;

    setFormData((prev) => ({
      ...prev,
      phone: formatPhoneNumber(cleaned),
    }));

    if (errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: "",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      handlePhoneChange(e);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  const handlePrivacyChange = (e) => {
    setIsPrivacyAccepted(e.target.checked);
    if (errors.privacy) {
      setErrors((prev) => ({
        ...prev,
        privacy: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "Введите имя";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "Имя должно содержать минимум 2 символа";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Введите фамилию";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Фамилия должна содержать минимум 2 символа";
    }

    const phoneCleaned = formData.phone.replace(/\D/g, "");
    if (!phoneCleaned) {
      newErrors.phone = "Введите номер телефона";
    } else if (phoneCleaned.length !== 11) {
      newErrors.phone = "Номер телефона должен содержать 11 цифр";
    } else if (!phoneCleaned.startsWith("7") && !phoneCleaned.startsWith("8")) {
      newErrors.phone = "Введите корректный номер телефона";
    }

    if (!isPrivacyAccepted) {
      newErrors.privacy = "Подтвердите согласие с политикой конфиденциальности";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        const phoneCleaned = formData.phone.replace(/\D/g, "");

        const feedbackData = {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          phone: phoneCleaned,
          is_processed: 0,
        };

        const response = await submitFeedback(feedbackData);

        if (onFormSubmit) {
          await onFormSubmit(response);
        }

        setFormData({
          firstName: "",
          lastName: "",
          phone: "",
        });
        setIsPrivacyAccepted(false); // Сбрасываем чекбокс

        onClose();

        alert(
          "Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.",
        );
      } catch (error) {
        console.error("Ошибка при отправке формы:", error);
        setErrors((prev) => ({
          ...prev,
          submit: "Ошибка при отправке формы. Попробуйте позже.",
        }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = (e) => {
    if (
      e.target === e.currentTarget ||
      e.target.closest(`.${styles.popupClose}`)
    ) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.popupOverlay} onClick={handleClose}>
      <div className={styles.popupContent}>
        <button
          className={styles.popupClose}
          onClick={onClose}
          aria-label="Закрыть!"
        >
          &times;
        </button>

        <div className={styles.popupHeader}>
          <h2 className={styles.popupTitle}>
            Сделай первый шаг -<br />
            <span className={styles.popupTitleHighlight}>ОТПРАВЬ ЗАЯВКУ</span>
          </h2>
        </div>

        <form className={styles.popupForm} onSubmit={handleSubmit} noValidate>
          <div className={styles.formGroup}>
            <label htmlFor="firstName" className={styles.formLabel}>
              Имя
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`${styles.formInput} ${
                errors.firstName ? styles.error : ""
              }`}
              placeholder="Введите ваше имя"
              disabled={isSubmitting}
              maxLength="50"
            />
            {errors.firstName && (
              <span className={styles.errorMessage}>{errors.firstName}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="lastName" className={styles.formLabel}>
              Фамилия
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`${styles.formInput} ${
                errors.lastName ? styles.error : ""
              }`}
              placeholder="Введите вашу фамилию"
              disabled={isSubmitting}
              maxLength="50"
            />
            {errors.lastName && (
              <span className={styles.errorMessage}>{errors.lastName}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="phone" className={styles.formLabel}>
              Телефон
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className={`${styles.formInput} ${
                errors.phone ? styles.error : ""
              }`}
              placeholder="+7 (___) ___-__-__"
              disabled={isSubmitting}
              maxLength="18"
            />
            {errors.phone && (
              <span className={styles.errorMessage}>{errors.phone}</span>
            )}
          </div>

          {/* Кастомный чекбокс */}
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isPrivacyAccepted}
                onChange={handlePrivacyChange}
                className={styles.checkboxInput}
                disabled={isSubmitting}
              />
              <span className={styles.checkboxCustom}></span>
              <span className={styles.checkboxText}>
                Я соглашаюсь с{" "}
                <a
                  href={privacyPolicyPdf}
                  className={styles.privacyLinkInline}
                  onClick={openPrivacyPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  политикой конфиденциальности
                </a>
              </span>
            </label>
            {errors.privacy && (
              <span className={styles.errorMessage}>{errors.privacy}</span>
            )}
          </div>

          {errors.submit && (
            <div className={styles.submitError}>{errors.submit}</div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || !isPrivacyAccepted}
          >
            {isSubmitting ? (
              <span className={styles.submitButton__text}>Отправка...</span>
            ) : (
              <span className={styles.submitButton__text}>Отправить</span>
            )}
          </button>
        </form>

        <div className={styles.popupFooter}>
          <p className={styles.privacyText}>
            Нажимая кнопку "Отправить", вы соглашаетесь с нашей{" "}
            <a
              href={privacyPolicyPdf}
              className={styles.privacyLink}
              onClick={openPrivacyPolicy}
              target="_blank"
              rel="noopener noreferrer"
            >
              Политикой конфиденциальности
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Popup;
